import React, { useCallback, useEffect, useState } from 'react';
import { Space, Table, Tag, Button, Card, Input, Flex, Select, Modal, Form, Popconfirm } from 'antd';
import { DeleteOutlined, EditOutlined, KeyOutlined, CopyOutlined } from '@ant-design/icons';
import { useForm, useWatch } from 'antd/es/form/Form';
import UserService from 'services/user';
import { useApp } from 'contexts/app';
import { useAuth } from 'contexts/auth';
import _ from 'lodash';

const { Search } = Input;
const service = new UserService();

const delaySearch = _.debounce((fn, data) => fn(data), 500, { leading: false });

const permissions = [
    { label: 'Quản lý sản phẩm', value: 'product.all' },
    { label: 'Quản lý đơn hàng', value: 'order.all' },
    { label: 'Quản lý khách hàng', value: 'customer.all' },
    { label: 'Quản lý công nợ', value: 'debt.all' },
    { label: 'Quản lý voucher', value: 'voucher.all' },
    { label: 'Cấu hình shop', value: 'setting.all' },
];

const formatLoginIdForDisplay = (loginId = '') => {
    const normalized = String(loginId || '');
    if (!normalized.includes('@')) {
        return normalized;
    }
    return normalized.split('@')[0] || normalized;
};

const buildLoginInfo = (record, pass) => {
    const wID = record?.work_space?.w_id || '';
    const loginId = formatLoginIdForDisplay(record?.login_id || '');
    const link = `${window.location.origin}/login/${wID}`;
    let info = `Link đăng nhập: ${link}`;
    info += `\nTài khoản đăng nhập: ${loginId}`;
    if (pass) {
        info += `\nMật khẩu: ${pass}`;
    }
    return info;
};

const UserList = () => {
    const [data, setData] = useState([]);
    const { showNotify, showLoading, message } = useApp();
    const [filter, setFilter] = useState({});
    const [modal, setModal] = useState({ add: false, update: false, reset: false });
    const [updateUser, setUpdateUser] = useState();
    const [resetUser, setResetUser] = useState();
    const { user } = useAuth();

    const onSearch = useCallback(async (searchFilter) => {
        try {
            const list = await service.listEmployees({ info: searchFilter?.info });
            setData(list);
        } catch (error) {
            message.error(error?.message || 'Không thể tải danh sách nhân viên');
        }
    }, [message]);

    useEffect(() => {
        delaySearch(onSearch, filter);
    }, [filter, onSearch]);

    const setFilterValue = useCallback((name, value) => {
        setFilter((prev) => ({ ...prev, [name]: value }));
    }, []);

    const loadData = useCallback(async () => {
        await onSearch(filter);
    }, [filter, onSearch]);

    const disableUser = useCallback(async (id) => {
        showLoading(true);
        try {
            await service.disableEmployee(id);
            showNotify('Đã khoá tài khoản nhân viên');
            await loadData();
        } catch (error) {
            message.error(error?.message || 'Không thể khoá tài khoản');
        }
        showLoading(false);
    }, [loadData, message, showLoading, showNotify]);

    const openModal = useCallback((type, selectedUser) => {
        setModal((prev) => ({ ...prev, [type]: true }));
        if (type === 'update') {
            setUpdateUser(selectedUser);
        }
        if (type === 'reset') {
            setResetUser(selectedUser);
        }
    }, []);

    const closeModal = useCallback((type) => {
        setModal((prev) => ({ ...prev, [type]: false }));
        if (type === 'update') {
            setUpdateUser(undefined);
        }
        if (type === 'reset') {
            setResetUser(undefined);
        }
    }, []);

    const copyLoginInfo = useCallback(async (record) => {
        try {
            await navigator.clipboard.writeText(buildLoginInfo(record, record?.default_password));
            message.success('Đã copy thông tin đăng nhập');
        } catch (error) {
            message.error('Không thể copy thông tin');
        }
    }, [message]);

    const columns = [
        {
            title: 'Họ tên',
            dataIndex: 'full_name',
            key: 'full_name',
        },
        {
            title: 'Tài khoản',
            key: 'login_id',
            render: (_, record) => formatLoginIdForDisplay(record?.login_id),
        },
        {
            title: 'Số điện thoại',
            dataIndex: 'phone',
            key: 'phone',
            render: (phone) => phone || '-',
        },
        {
            title: 'Trạng thái',
            key: 'status',
            render: (_, record) => (
                record?.is_disabled
                    ? <Tag color="red">Không kích hoạt</Tag>
                    : <Tag color="green">Kích hoạt</Tag>
            ),
        },
        {
            title: 'Vai trò',
            key: 'role',
            render: (_, record) => record.work_space?.role || '-',
        },
        {
            title: 'Phân quyền',
            key: 'permissions',
            render: (_, record) => (
                <Space wrap>
                    {(record.work_space?.permissions ?? []).map((permission) => {
                        const permissionInfo = permissions.find((item) => item.value === permission);
                        return permissionInfo ? <Tag key={permission}>{permissionInfo.label}</Tag> : null;
                    })}
                </Space>
            ),
        },
        {
            title: '',
            key: 'action',
            render: (_, record) => (
                <Space size="middle">
                    {user.id !== record.id && (
                        <>
                            <Button type="link" onClick={() => copyLoginInfo(record)}><CopyOutlined /></Button>
                            <Button type="link" onClick={() => openModal('update', record)}><EditOutlined /></Button>
                            <Button type="link" onClick={() => openModal('reset', record)}><KeyOutlined /></Button>
                            <Popconfirm onConfirm={() => disableUser(record.id)} title="Bạn có chắc chắn khoá tài khoản này?">
                                <Button type="link"><DeleteOutlined /></Button>
                            </Popconfirm>
                        </>
                    )}
                </Space>
            ),
        },
    ];

    return (
        <Space direction="vertical" size={16} style={{ width: '100%' }}>
            <Card style={{ width: '100%' }}>
                <Space direction="vertical" size={8} style={{ width: '100%' }}>
                    <Search
                        placeholder="Tìm tên, tài khoản, số điện thoại"
                        onChange={(e) => setFilterValue('info', e.target.value)}
                        style={{ width: '100%' }}
                        value={filter.info}
                    />
                    <Flex justify="flex-end" style={{ width: '100%' }}>
                        <Button type="primary" onClick={() => openModal('add')}>Thêm nhân viên</Button>
                    </Flex>
                </Space>
            </Card>
            <Card>
                <Table columns={columns} dataSource={data} rowKey={(record) => record.id} />
            </Card>
            <AddNewUser open={modal.add} onClose={() => closeModal('add')} onSaved={loadData} />
            <UpdateUser open={modal.update} onClose={() => closeModal('update')} user={updateUser} onSaved={loadData} />
            <ResetEmployeePassword open={modal.reset} onClose={() => closeModal('reset')} user={resetUser} onSaved={loadData} />
        </Space>
    );
};

const AddNewUser = ({ open, onClose, onSaved }) => {
    const [form] = useForm();
    const { showLoading, showNotify, message } = useApp();
    const { user } = useAuth();

    useEffect(() => {
        if (!open) {
            form.resetFields();
        }
    }, [form, open]);

    const onFinish = async () => {
        const values = form.getFieldsValue();
        const payload = {
            full_name: values.full_name,
            username: values.username,
            pass: values.pass,
            role: values.role,
            permissions: values.role === 'admin' ? [] : (values.permissions || []),
            phone: values.phone,
        };

        showLoading(true);
        try {
            const createdUser = await service.createEmployee(payload);
            try {
                await navigator.clipboard.writeText(buildLoginInfo({ ...createdUser, work_space: { w_id: user?.work_space?.w_id } }, values.pass));
            } catch (error) {
            }
            showNotify('Đã tạo tài khoản nhân viên');
            form.resetFields();
            onClose();
            onSaved && onSaved();
        } catch (error) {
            message.error(error?.message || 'Không thể tạo tài khoản nhân viên');
        }
        showLoading(false);
    };

    return (
        <Modal open={open} title="Thêm nhân viên" onClose={onClose} onCancel={onClose} onOk={() => form.submit()}>
            <Form layout="vertical" form={form} initialValues={{ role: 'staff', permissions: [] }} onFinish={onFinish}>
                <Form.Item
                    label="Họ tên"
                    name="full_name"
                    rules={[{ required: true, message: 'Bạn phải nhập họ tên' }]}
                >
                    <Input placeholder="Họ tên"></Input>
                </Form.Item>
                <Form.Item
                    label="Tên đăng nhập"
                    name="username"
                    rules={[
                        { required: true, message: 'Bạn phải nhập tên đăng nhập' },
                        { pattern: /^[a-zA-Z0-9._-]+$/, message: 'Tên đăng nhập chỉ gồm chữ, số, ., _, -' },
                    ]}
                >
                    <Input placeholder="vd: nhanvien01"></Input>
                </Form.Item>
                <Form.Item
                    label="Số điện thoại"
                    name="phone"
                    rules={[
                        { pattern: /^[0-9]{10}$/, message: 'Số điện thoại không đúng định dạng' },
                    ]}
                >
                    <Input placeholder="Số điện thoại"></Input>
                </Form.Item>
                <Form.Item
                    label="Mật khẩu"
                    name="pass"
                    rules={[{ required: true, message: 'Bạn phải nhập mật khẩu' }]}
                >
                    <Input.Password placeholder="Mật khẩu"></Input.Password>
                </Form.Item>
                <Form.Item
                    label="Nhập lại mật khẩu"
                    name="re_pass"
                    dependencies={['pass']}
                    rules={[
                        { required: true, message: 'Bạn phải nhập lại mật khẩu' },
                        ({ getFieldValue }) => ({
                            validator(_, value) {
                                if (!value || getFieldValue('pass') === value) {
                                    return Promise.resolve();
                                }
                                return Promise.reject(new Error('Mật khẩu nhập lại không khớp'));
                            },
                        }),
                    ]}
                >
                    <Input.Password placeholder="Nhập lại mật khẩu"></Input.Password>
                </Form.Item>
                <UserFields></UserFields>
            </Form>
        </Modal>
    );
};

const UpdateUser = ({ open, onClose, user, onSaved }) => {
    const [form] = useForm();
    const { showLoading, showNotify, message } = useApp();

    useEffect(() => {
        if (user) {
            form.setFieldsValue({
                ...(user.work_space || {}),
                full_name: user.full_name,
                is_disabled: user.is_disabled === true,
            });
        } else {
            form.resetFields();
        }
    }, [form, open, user]);

    const onFinish = async () => {
        const data = { ...form.getFieldsValue(), user_id: user.id };
        if (data.role === 'admin') {
            data.permissions = [];
        }
        showLoading(true);
        try {
            await service.updateUserRole(data);
            showNotify('Đã cập nhật quyền');
            onClose();
            onSaved && onSaved();
        } catch (error) {
            message.error(error?.message || 'Không thể cập nhật quyền');
        }
        showLoading(false);
    };

    return (
        <Modal open={open} title="Cập nhật quyền người dùng" onClose={onClose} onCancel={onClose} onOk={() => form.submit()}>
            <Form layout="vertical" form={form} initialValues={{}} onFinish={onFinish}>
                <Form.Item
                    label="Họ tên"
                    name="full_name"
                    rules={[{ required: true, message: 'Bạn phải nhập họ tên' }]}
                >
                    <Input placeholder="Họ tên"></Input>
                </Form.Item>

                <Form.Item
                    label="Trạng thái tài khoản"
                    name="is_disabled"
                    rules={[{ required: true, message: 'Bạn phải chọn trạng thái' }]}
                >
                    <Select
                        options={[
                            { label: 'Kích hoạt', value: false },
                            { label: 'Không kích hoạt', value: true },
                        ]}
                    ></Select>
                </Form.Item>

                <UserFields></UserFields>
            </Form>
        </Modal>
    );
};

const ResetEmployeePassword = ({ open, onClose, user, onSaved }) => {
    const [form] = useForm();
    const { showLoading, showNotify, message } = useApp();

    useEffect(() => {
        form.resetFields();
    }, [form, open, user]);

    const onFinish = async () => {
        if (!user?.id) {
            return;
        }

        const values = form.getFieldsValue();
        showLoading(true);
        try {
            await service.resetEmployeePass({ user_id: user.id, pass: values.pass });
            showNotify('Đã đặt lại mật khẩu');
            onClose();
            onSaved && onSaved();
        } catch (error) {
            message.error(error?.message || 'Không thể đặt lại mật khẩu');
        }
        showLoading(false);
    };

    return (
        <Modal
            open={open}
            title={`Đặt lại mật khẩu: ${formatLoginIdForDisplay(user?.login_id || '')}`}
            onCancel={onClose}
            onClose={onClose}
            onOk={() => form.submit()}
        >
            <Form layout="vertical" form={form} onFinish={onFinish}>
                <Form.Item
                    label="Mật khẩu mới"
                    name="pass"
                    rules={[{ required: true, message: 'Bạn phải nhập mật khẩu mới' }]}
                >
                    <Input.Password placeholder="Mật khẩu mới"></Input.Password>
                </Form.Item>
                <Form.Item
                    label="Nhập lại mật khẩu mới"
                    name="re_pass"
                    dependencies={['pass']}
                    rules={[
                        { required: true, message: 'Bạn phải nhập lại mật khẩu' },
                        ({ getFieldValue }) => ({
                            validator(_, value) {
                                if (!value || getFieldValue('pass') === value) {
                                    return Promise.resolve();
                                }
                                return Promise.reject(new Error('Mật khẩu nhập lại không khớp'));
                            },
                        }),
                    ]}
                >
                    <Input.Password placeholder="Nhập lại mật khẩu"></Input.Password>
                </Form.Item>
            </Form>
        </Modal>
    );
};

const UserFields = () => {
    const form = Form.useFormInstance();
    const role = useWatch('role', form);

    return (
        <>
            <Form.Item
                label="Vai trò"
                name="role"
                rules={[{ required: true, message: 'Bạn phải chọn vai trò' }]}
            >
                <Select
                    options={[
                        { label: 'Quản trị viên', value: 'admin' },
                        { label: 'Nhân viên', value: 'staff' },
                    ]}
                ></Select>
            </Form.Item>
            {role !== 'admin' && (
                <Form.Item
                    label="Phân quyền"
                    name="permissions"
                    rules={[{ required: true, message: 'Bạn phải chọn ít nhất 1 quyền' }]}
                >
                    <Select options={permissions} mode="multiple"></Select>
                </Form.Item>
            )}
        </>
    );
};

export default UserList;